import {
  AuthError,
  ConfirmationResult,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  FacebookAuthProvider,
  getRedirectResult,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  unlink,
  updateEmail,
  updatePassword,
  updateProfile,
  User,
  UserCredential,
} from "firebase/auth";
import { auth } from "./firebase";
import { clearTosViewBypasses } from "./tosBypass";

// Types
export interface AuthErrorWithCode extends AuthError {
  code: string;
}

export interface UserProfile {
  displayName?: string;
  photoURL?: string;
}

// Email and Password Authentication
export const signInWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const signUpWithEmail = async (
  email: string,
  password: string,
  displayName?: string
): Promise<UserCredential> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }

    return userCredential;
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const updateUserPassword = async (newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");
    await updatePassword(user, newPassword);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const updateUserEmail = async (newEmail: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");
    await updateEmail(user, newEmail);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const updateUserProfile = async (profile: UserProfile): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");
    await updateProfile(user, profile);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Phone Number Authentication
export const signInWithPhone = async (
  phoneNumber: string,
  appVerifier: RecaptchaVerifier
): Promise<ConfirmationResult> => {
  try {
    return await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const confirmPhoneCode = async (
  confirmationResult: ConfirmationResult,
  code: string
): Promise<UserCredential> => {
  try {
    return await confirmationResult.confirm(code);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Google Authentication
export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const provider = new GoogleAuthProvider();
    return await signInWithPopup(auth, provider);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const signInWithGoogleRedirect = async (): Promise<void> => {
  try {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Microsoft Authentication
export const signInWithMicrosoft = async (): Promise<UserCredential> => {
  try {
    const provider = new OAuthProvider("microsoft.com");
    return await signInWithPopup(auth, provider);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Redirect Result Handler
export const handleRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    return await getRedirectResult(auth);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Account Linking
export const linkWithGoogle = async (): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // Note: Account linking requires additional setup and credential handling
    // For now, we'll just return the sign-in result
    return result;
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const linkWithApple = async (): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const provider = new OAuthProvider("apple.com");
    const result = await signInWithPopup(auth, provider);
    // Note: Account linking requires additional setup and credential handling
    // For now, we'll just return the sign-in result
    return result;
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const linkWithFacebook = async (): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const provider = new FacebookAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // Note: Account linking requires additional setup and credential handling
    // For now, we'll just return the sign-in result
    return result;
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const linkWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const credential = EmailAuthProvider.credential(email, password);
    return await linkWithCredential(user, credential);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const linkWithPhone = async (phoneNumber: string, appVerifier: RecaptchaVerifier): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    // Note: You'll need to handle the confirmation code separately
    throw new Error("Phone linking requires confirmation code handling");
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Account Unlinking
export const unlinkProvider = async (providerId: string): Promise<User> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    return await unlink(user, providerId);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Reauthentication
export const reauthenticateWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const credential = EmailAuthProvider.credential(email, password);
    return await reauthenticateWithCredential(user, credential);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

export const reauthenticateWithGoogle = async (): Promise<UserCredential> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // Note: Reauthentication with Google requires additional credential handling
    // For now, we'll just return the sign-in result
    return result;
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Account Management
export const deleteCurrentUser = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");

    await deleteUser(user);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Sign Out
export const signOutUser = async (): Promise<void> => {
  try {
    // Clear every org's ToS bypass so the user will be prompted for ToS on next login
    clearTosViewBypasses();
    await signOut(auth);
  } catch (error) {
    throw error as AuthErrorWithCode;
  }
};

// Auth State Observer
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Utility Functions
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

export const isUserSignedIn = (): boolean => {
  return auth.currentUser !== null;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): { isValid: boolean; message: string } => {
  if (password.length < 6) {
    return { isValid: false, message: "Password must be at least 6 characters long" };
  }
  return { isValid: true, message: "Password is valid" };
};

// Error Handling Helper
export const getAuthErrorMessage = (error: AuthErrorWithCode): string => {
  switch (error.code) {
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed before completion.";
    case "auth/cancelled-popup-request":
      return "Sign-in popup was cancelled.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked by the browser.";
    case "auth/account-exists-with-different-credential":
      return "An account already exists with the same email but different sign-in credentials.";
    case "auth/requires-recent-login":
      return "This operation requires recent authentication. Please sign in again.";
    default:
      return error.message || "An authentication error occurred.";
  }
};
